/*
 * Copyright © 2020-2025 XCamera. All Rights Reserved.
 * @Description:
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-08-26 14:55:09
 */
import React from 'react'

/**
 * 隐患生成来源选择模式枚举
 */
export enum XCHazardSourceMode {
  watermark = 'watermark',
  image = 'image'
}

export enum XCMonitorType {
  image = 0,
  watermark = 1
}

/**
 * 隐患生成来源模式标签映射
 */
export const HAZARD_SOURCE_MODE_LABELS = {
  [XCHazardSourceMode.watermark]: '依据水印识别',
  [XCHazardSourceMode.image]: '依据图像识别'
} as const

/**
 * 隐患生成来源名称到显示文本的映射
 */
export const HAZARD_SOURCE_NAME_LABELS = {
  llm: '依据图像识别',
  watermark: '依据水印描述'
} as const

/**
 * 隐患生成来源选择器属性接口
 */
export interface XCHazardSourceSelectorProps {
  /** 可选项列表 - 对象数组，包含 name 和 val 属性 */
  options?: Array<{ name: string; val: string }>
  /** 当前选中值 - 数组索引 */
  value?: number
  /** 值变化回调 - 传递选中项的索引 */
  onChange?: (index: number) => void
  /** 自定义样式 */
  style?: React.CSSProperties
  /** 是否禁用 */
  disabled?: boolean
}

/**
 * 隐患生成来源选择组件
 * 提供可配置的选项选择，支持对象数组格式的选项
 */
const HazardSourceSelector: React.FC<XCHazardSourceSelectorProps> = ({
  options = [
    { name: 'llm', val: '0' },
    { name: 'watermark', val: '1' }
  ],
  value,
  onChange,
  style,
  disabled = false
}) => {
  /**
   * 获取选项的显示文本
   */
  const getDisplayText = (optionName: string): string => {
    return (
      HAZARD_SOURCE_NAME_LABELS[
        optionName as keyof typeof HAZARD_SOURCE_NAME_LABELS
      ] || optionName
    )
  }
  // 如果 options 数组为空，不渲染组件
  if (!options || options.length === 0) {
    return null
  }

  // 如果数组只有一个选项且name为'llm'，不展示组件
  if (options.length === 1 && options[0].name === 'llm') {
    return null
  }

  // 如果没有传入value，默认选择第一个选项的索引
  const currentValue = value !== undefined ? value : 0

  /**
   * 处理选项变化
   */
  const handleOptionChange = (index: number) => {
    // 只有在选择的值与当前值不同时才触发onChange回调
    if (!disabled && onChange && index !== currentValue) {
      onChange(index)
    }
  }

  // 自定义样式，使用div包装以实现更好的样式控制
  return (
    <div
      style={{
        display: 'inline-flex',
        justifyContent: 'center',
        border: '1px solid #d9d9d9',
        borderRadius: '4px',
        overflow: 'hidden',
        marginTop: 4,
        marginLeft: 12,
        ...style
      }}
    >
      {options.map((option, index) => {
        const isSelected = currentValue === index
        return (
          <div
            key={`${option.name}-${index}`}
            style={{
              padding: '2px 12px',
              cursor: disabled ? 'not-allowed' : 'pointer',
              background: isSelected ? '#fff' : '#f5f5f5',
              color: isSelected ? '#333' : '#999',
              fontWeight: isSelected ? 'bold' : 'normal',
              borderRight:
                index < options.length - 1 ? '1px solid #d9d9d9' : 'none',
              fontSize: '14px',
              opacity: disabled ? 0.5 : 1
            }}
            onClick={() => !disabled && handleOptionChange(index)}
          >
            {getDisplayText(option.name)}
          </div>
        )
      })}
    </div>
  )
}

export default HazardSourceSelector
